import {useCallback, useEffect, useMemo, useState} from 'react';
import buildUrl from '@shared/helpers/buildUrl';
import {MultipleFetchOptions, Request} from '@shared/hooks/types';
import {RawKopaCode} from '@shared/types/codes';
import toQueryString from '@vp-shared/utils/toQueryString';
import useMultipleFetch from '@shared/hooks/useMultipleFetch';
/**
 * Handle kopa codes fetching
 * @param apiUrl Endpoint for kopa codes
 * @param codeSets code-sets to fetch, for example "KUNTA"
 * @returns {{
 *    kopaCodeSets: codeSets,
 *    isLoading: true when request active,
 *    fetchKopaCodeSets: callback for sending the get request,
 *    error: error from useFetch or null if no error
 * }}
 */
type RawKopaCodeSet = {
  name: string;
  codes: RawKopaCode[];
};
const useKopaCodeSets = (apiUrl: string, codeSets: string[]) => {
  const [kopaCodeSets, setKopaCodeSets] = useState<RawKopaCodeSet[] | null>(
    null
  );

  const today = useMemo(() => {
    return `${new Date(Date.now()).toISOString().split('T')[0]}`;
  }, []);

  const fullApiUrls = useMemo(
    () =>
      codeSets.map(cs =>
        buildUrl(apiUrl, cs + `/koodit?${toQueryString({voimassa: today})}`)
      ),
    [apiUrl, codeSets, today]
  );

  const requestSpecs: Request[] = useMemo(
    () => fullApiUrls.map(url => ({url: url, method: 'GET'} as Request)),
    [fullApiUrls]
  );

  const fetchOpts: MultipleFetchOptions = useMemo(
    () => ({
      sender: 'Hakupalvelu',
      request: requestSpecs
    }),
    [requestSpecs]
  );
  const {doRequest, data, isLoading, error} =
    useMultipleFetch<RawKopaCode[]>(fetchOpts);

  const fetchKopaCodeSets = useCallback(() => {
    doRequest(requestSpecs);
  }, [doRequest, requestSpecs]);

  useEffect(() => {
    if (!isLoading) {
      const kopaCodesData =
        data?.map(d => {
          return {
            name: getCodeSetName(d.entityName),
            codes: d.result
          };
        }) ?? [];
      setKopaCodeSets(kopaCodesData);
    }
  }, [data, isLoading]);

  return {kopaCodeSets, isLoading, fetchKopaCodeSets, error};
};
const getCodeSetName = (url: string) => {
  return decodeURI(url).split('kopa/')[1]?.split('/koodit')[0];
};
export default useKopaCodeSets;
